import { Order, OrderDetail, ProductOrder } from '@interfaces/OrderTypes';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { DELIVERY_MAP, ORDER_PAYMENT_MAP } from '@utils/constants';
import { Commerce } from '@interfaces/general';
import { formatToCurrency } from './formats';

declare let pdfMake: {
  createPdf: (pdf: TDocumentDefinitions) => {
    print: () => void;
  };
};

type OrderType = Pick<
  Order,
  | 'folio'
  | 'nombre'
  | 'numero_celular'
  | 'numero_mesa'
  | 'subtotal'
  | 'coupon_amount'
  | 'propina'
  | 'costo_envio'
  | 'total'
  | 'cliente_paga_con'
  | 'metodo_entrega'
  | 'metodo_pago'
  | 'numero_interior'
  | 'numero_exterior'
  | 'referencias'
  | 'colonia'
  | 'calle'
  | 'created_at'
  | 'comentario'
  | 'direccion_completa'
>;

type TextSizeType = {
  title: number;
  subTitle: number;
  content: number;
};

const createClientInfoSection = (order: OrderType, textSize: TextSizeType, printDecoration: boolean) => {
  const rows = [];

  rows.push([
    {
      text: 'Ticket pedido',
      fontSize: textSize.title,
      bold: true,
      alignment: 'center',
    },
  ]);

  rows.push([
    {
      text: 'No. ' + order.folio,
      fontSize: textSize.title,
      bold: true,
      alignment: 'center',
    },
  ]);

  if (order.nombre) {
    rows.push([
      {
        text: 'Cliente:',
        fontSize: textSize.content,
        bold: true,
        alignment: 'center',
        fillColor: printDecoration ? '#F1F1F1' : undefined,
      },
    ]);

    rows.push([
      {
        text: order.nombre,
        fontSize: textSize.subTitle,
        bold: true,
        alignment: 'center',
        fillColor: printDecoration ? '#F1F1F1' : undefined,
      },
    ]);
  }

  if (order.numero_celular) {
    rows.push([
      {
        text: `Celular: ${order.numero_celular}`,
        fontSize: textSize.content,
        bold: true,
        alignment: 'center',
        fillColor: printDecoration ? '#F1F1F1' : undefined,
      },
    ]);
  }

  if (order.numero_mesa) {
    rows.push([
      {
        text: 'Mesa: ' + order.numero_mesa,
        fontSize: textSize.content,
        bold: true,
        alignment: 'center',
        fillColor: printDecoration ? '#F1F1F1' : undefined,
      },
    ]);
  }

  rows.push([
    {
      text: 'Detalle del pedido:',
      fontSize: textSize.content,
      bold: true,
      alignment: 'center',
      margin: [0, 5, 0, 0],
    },
  ]);

  return rows;
};

const createDeliveryAddressSection = (order: OrderType, textSize: TextSizeType, referencesLabel: string) => {
  const rows = [];

  if (order.metodo_entrega === 'delivery') {
    rows.push([
      {
        text: 'Domicilio de entrega:',
        fontSize: textSize.content,
        bold: true,
        alignment: 'left',
      },
    ]);
  }

  if (order.colonia) {
    rows.push([
      {
        text: 'Zona o Colonia: ' + order.colonia,
        fontSize: textSize.content,
        alignment: 'left',
      },
    ]);
  }

  if (order.calle) {
    rows.push([
      {
        text: 'Calle o Mza: ' + order.calle,
        fontSize: textSize.content,
        alignment: 'left',
      },
    ]);
  }

  if (order.numero_exterior) {
    rows.push([
      {
        text: 'Numero Exterior: ' + order.numero_exterior,
        fontSize: textSize.content,
        alignment: 'left',
      },
    ]);
  }

  if (order.numero_interior) {
    rows.push([
      {
        text: 'Numero interior: ' + order.numero_interior,
        fontSize: textSize.content,
        alignment: 'left',
      },
    ]);
  }

  if (order.direccion_completa) {
    rows.push([
      {
        text: order.direccion_completa,
        fontSize: textSize.content,
        alignment: 'left',
      },
    ]);
  }

  if (order.referencias) {
    rows.push([
      {
        text: `${referencesLabel}: ${order.referencias}`,
        fontSize: textSize.content,
        alignment: 'left',
      },
    ]);
  }

  return rows;
};

const createProductsTable = (products: ProductOrder[], textSize: TextSizeType, printDecoration: boolean) => {
  const rows = [];

  rows.push([
    {
      text: 'Artículo',
      fontSize: textSize.content,
      bold: true,
      fillColor: printDecoration ? '#F1F1F1' : undefined,
      margin: [3, 0, 0, 0],
    },
    {
      text: 'Total',
      fontSize: textSize.content,
      bold: true,
      fillColor: printDecoration ? '#F1F1F1' : undefined,
      alignment: 'right',
      margin: [0, 0, 3, 0],
    },
  ]);

  products.forEach((prd, idx) => {
    rows.push([
      {
        text: `${prd.cantidad} x ${prd.nombre} - ${prd.presentacion}`,
        fontSize: textSize.content,
        margin: [3, 0, 0, 0],
        border: idx > 0 ? [false, true, false, false] : undefined,
      },
      {
        text: `$${prd.subtotal}`,
        fontSize: textSize.content,
        bold: true,
        alignment: 'right',
        margin: [0, 0, 3, 0],
        border: idx > 0 ? [false, true, false, false] : undefined,
      },
    ]);

    if (prd.instrucciones) {
      rows.push([
        {
          text: `Instrucciones: ${prd.instrucciones}`,
          fontSize: textSize.content,
          colSpan: 2,
          bold: true,
          alignment: 'justify',
          margin: [3, 0, 0, 0],
        },
        {},
      ]);
    }

    prd.extras.forEach((extra) => {
      rows.push([
        {
          text: `${extra.cantidad} x ${extra.nombre}`,
          fontSize: textSize.content,
          margin: [5, 0, 0, 0],
        },
        {
          text: `${formatToCurrency(Number(extra.precio))}`,
          fontSize: textSize.content,
          alignment: 'right',
          margin: [0, 0, 3, 0],
        },
      ]);
    });
  });

  return rows;
};

const createTotalsSection = (order: OrderType, textSize: TextSizeType, printDecoration: boolean) => {
  const rows = [];

  rows.push([
    {
      text: 'Subtotal',
      fontSize: textSize.content,
      alignment: 'right',
      border: [false, true, false, false],
    },
    {
      text: `${formatToCurrency(order.subtotal)}`,
      alignment: 'right',
      fontSize: textSize.content,
      margin: [0, 0, 3, 0],
      border: [false, true, false, false],
    },
  ]);

  if (order.coupon_amount) {
    rows.push([
      {
        text: 'Descuento',
        fontSize: textSize.content,
        alignment: 'right',
        border: [false, true, false, false],
      },
      {
        text: `- ${formatToCurrency(order.coupon_amount)}`,
        fontSize: textSize.content,
        alignment: 'right',
        border: [false, true, false, false],
      },
    ]);
  }

  if (order.propina) {
    rows.push([
      {
        text: 'Propina',
        alignment: 'right',
        fontSize: textSize.content,
        fillColor: printDecoration ? '#F1F1F1' : undefined,
      },
      {
        text: `${formatToCurrency(order.propina)}`,
        alignment: 'right',
        fontSize: textSize.content,
        margin: [0, 0, 3, 0],
        fillColor: printDecoration ? '#F1F1F1' : undefined,
      },
    ]);
  }

  if (order.metodo_entrega === 'delivery') {
    rows.push([
      {
        text: 'Costo de envío',
        alignment: 'right',
        fontSize: textSize.content,
      },
      {
        text: `${formatToCurrency(Number(order.costo_envio))}`,
        alignment: 'right',
        fontSize: textSize.content,
        margin: [0, 0, 3, 0],
      },
    ]);
  }

  rows.push([
    {
      text: 'Total a pagar',
      alignment: 'right',
      fontSize: textSize.content,
      bold: true,
      fillColor: printDecoration ? '#F1F1F1' : undefined,
    },
    {
      text: `${formatToCurrency(Number(order.total))}`,
      alignment: 'right',
      fontSize: textSize.content,
      bold: true,
      margin: [0, 0, 3, 0],
      fillColor: printDecoration ? '#F1F1F1' : undefined,
    },
  ]);

  return rows;
};

const createFooterSection = (
  commerce: Pick<Commerce, 'nombre' | 'direccion' | 'address'>,
  order: OrderType,
  textSize: TextSizeType,
  printDecoration: boolean,
) => {
  const rows = [];

  if (commerce?.nombre) {
    rows.push([
      {
        text: commerce.nombre,
        fontSize: textSize.content,
        bold: true,
        alignment: 'center',
        margin: [0, 5, 0, 0],
      },
    ]);
  }

  if (commerce?.address?.fullAddress || commerce?.direccion) {
    rows.push([
      {
        text: 'Con domicilio en ' + (commerce?.address?.fullAddress || commerce?.direccion),
        fontSize: textSize.content,
        alignment: 'center',
      },
    ]);
  }

  rows.push([
    {
      text: 'Fecha y hora: ' + new Date(order.created_at).toLocaleString(),
      fontSize: textSize.content,
      bold: true,
      alignment: 'center',
      fillColor: printDecoration ? '#F1F1F1' : undefined,
    },
  ]);

  rows.push([
    {
      text: 'Este formato no es un comprobante de pago, ni contrato de venta, es para uso exclusivo del vendedor. Los precios que en el consignan son válidos hasta la fecha indicada.',
      fontSize: textSize.content,
      margin: 5,
      alignment: 'center',
    },
  ]);

  return rows;
};

const createPaymentInfoSection = (order: OrderType, textSize: TextSizeType, moneyChange: number | null) => {
  const rows = [];

  if (order.comentario) {
    rows.push([
      {
        text: 'Indicaciones: ' + order.comentario,
        fontSize: textSize.content,
        bold: true,
        alignment: 'left',
      },
    ]);
  }

  rows.push([
    {
      text: 'Metodo de entrega: ' + DELIVERY_MAP[order.metodo_entrega]?.label,
      fontSize: textSize.content,
      bold: true,
      alignment: 'left',
    },
  ]);

  const normalizedPaymentMethod =
    Object.entries(ORDER_PAYMENT_MAP).find(
      ([, value]) => value.actualKey === order.metodo_pago || value.key === order.metodo_pago,
    )?.[0] || order.metodo_pago;

  rows.push([
    {
      text: (() => {
        let paymentLabel = ORDER_PAYMENT_MAP[order.metodo_pago]?.label || order.metodo_pago;
        if (order.metodo_pago === ORDER_PAYMENT_MAP.toConfirm.key) {
          paymentLabel = order.metodo_entrega === 'inRestaurant' ? 'Por confirmar' : 'Pago en línea';
        }
        return 'Tipo de pago: ' + paymentLabel;
      })(),
      fontSize: textSize.content,
      bold: true,
      alignment: 'left',
    },
  ]);

  if (normalizedPaymentMethod === ORDER_PAYMENT_MAP.efectivo.key && order.cliente_paga_con) {
    rows.push([
      {
        text: 'Cliente paga con: ' + formatToCurrency(Number(order.cliente_paga_con)),
        fontSize: textSize.content,
        bold: true,
        alignment: 'left',
      },
    ]);
  }

  if (normalizedPaymentMethod === ORDER_PAYMENT_MAP.efectivo.key && order.cliente_paga_con && moneyChange !== null) {
    rows.push([
      {
        text: 'Cambio: ' + formatToCurrency(moneyChange),
        fontSize: textSize.content,
        bold: true,
        alignment: 'left',
      },
    ]);
  }

  return rows;
};

// Helper function that creates the content for a normal ticket
const createNormalTicketContent = (
  commerce: Pick<Commerce, 'nombre' | 'direccion' | 'address'>,
  order: OrderType,
  products: ProductOrder[],
  referencesLabel: string,
  textSize: TextSizeType,
  printDecoration: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] => {
  const normalizedPaymentMethod =
    Object.entries(ORDER_PAYMENT_MAP).find(
      ([, value]) => value.actualKey === order.metodo_pago || value.key === order.metodo_pago,
    )?.[0] || order.metodo_pago;

  const moneyChange: number | null =
    normalizedPaymentMethod === ORDER_PAYMENT_MAP.efectivo.key && order.cliente_paga_con
      ? parseFloat(order.cliente_paga_con) - parseFloat(String(order.total))
      : null;

  return [
    {
      layout: 'noBorders',
      table: {
        widths: ['100%'],
        body: createClientInfoSection(order, textSize, printDecoration),
      },
    },
    {
      layout: {
        hLineColor: '#6B6B6B',
        defaultBorder: false,
      },
      table: {
        widths: ['65%', '35%'],
        body: [
          ...createProductsTable(products, textSize, printDecoration),
          ...createTotalsSection(order, textSize, printDecoration),
        ],
      },
    },
    {
      layout: 'noBorders',
      table: {
        widths: ['100%'],
        body: [
          ...createPaymentInfoSection(order, textSize, moneyChange),
          ...createDeliveryAddressSection(order, textSize, referencesLabel),
        ],
      },
    },
    {
      layout: 'noBorders',
      table: {
        widths: ['100%'],
        body: createFooterSection(commerce, order, textSize, printDecoration),
      },
    },
  ];
};

export const generatePdf = async (
  commerce: Pick<Commerce, 'nombre' | 'direccion' | 'address'>,
  order: OrderType,
  products: ProductOrder[],
  referencesLabel: string,
) => {
  const printDecoration = localStorage.getItem('printDecoration') !== 'false';
  const TEXT_MAP = {
    small: { title: 7, subTitle: 6, content: 4 },
    medium: { title: 9, subTitle: 8, content: 6 },
    large: { title: 11, subTitle: 10, content: 8 },
    extraLarge: { title: 13, subTitle: 12, content: 10 },
  } as const;
  const textSize = TEXT_MAP[(localStorage.getItem('printTextSize') as keyof typeof TEXT_MAP) || 'medium'];

  const content = createNormalTicketContent(commerce, order, products, referencesLabel, textSize, printDecoration);

  const docDefinition: TDocumentDefinitions = {
    pageSize: {
      width: (125 / 58) * parseInt(localStorage.getItem('printPageWidth') || '58'),
      height: 'auto',
    },
    pageMargins: 5,
    content,
  };

  return pdfMake.createPdf(docDefinition);
};

// Simplified kitchen ticket - only products without prices
const createKitchenProductsTable = (products: ProductOrder[], textSize: TextSizeType) => {
  const rows = [];

  rows.push([
    {
      text: 'Productos',
      fontSize: textSize.content,
      bold: true,
      alignment: 'center',
    },
  ]);

  products.forEach((prd) => {
    rows.push([
      {
        text: `${prd.cantidad} x ${prd.nombre} - ${prd.presentacion}`,
        fontSize: textSize.content,
        bold: true,
        margin: [3, 3, 0, 0],
      },
    ]);

    if (prd.instrucciones) {
      rows.push([
        {
          text: `  Instrucciones: ${prd.instrucciones}`,
          fontSize: textSize.content,
          bold: true,
          alignment: 'left',
          margin: [10, 0, 0, 0],
        },
      ]);
    }

    prd.extras.forEach((extra) => {
      rows.push([
        {
          text: `  + ${extra.cantidad} x ${extra.nombre}`,
          fontSize: textSize.content,
          margin: [10, 0, 0, 0],
        },
      ]);
    });
  });

  return rows;
};

// Helper function that creates the content for a kitchen ticket
const createKitchenTicketContent = (
  commerce: Pick<Commerce, 'nombre'>,
  order: OrderType,
  products: ProductOrder[],
  textSize: TextSizeType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contentSections: any[] = [];

  // Header section with title and folio
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headerBody: any[] = [
    [
      {
        text: 'Ticket de Cocina',
        fontSize: textSize.title,
        bold: true,
        alignment: 'center',
      },
    ],
    [
      {
        text: 'No. ' + order.folio,
        fontSize: textSize.title,
        bold: true,
        alignment: 'center',
      },
    ],
  ];

  // Método de entrega
  headerBody.push([
    {
      text: `Método: ${DELIVERY_MAP[order.metodo_entrega]?.label || order.metodo_entrega}`,
      fontSize: textSize.content,
      bold: true,
      alignment: 'center',
    },
  ]);

  if (order.numero_mesa) {
    headerBody.push([
      {
        text: 'Mesa: ' + order.numero_mesa,
        fontSize: textSize.content,
        bold: true,
        alignment: 'center',
      },
    ]);
  }

  contentSections.push({
    layout: 'noBorders',
    table: {
      widths: ['100%'],
      body: headerBody,
    },
    margin: [0, 0, 0, 2],
  });

  // Products section
  contentSections.push({
    layout: 'noBorders',
    table: {
      widths: ['100%'],
      body: createKitchenProductsTable(products, textSize),
    },
    margin: [0, 0, 0, 2],
  });

  // Indicaciones section (optional)
  if (order.comentario) {
    contentSections.push({
      layout: 'noBorders',
      table: {
        widths: ['100%'],
        body: [
          [
            {
              text: 'Indicaciones: ' + order.comentario,
              fontSize: textSize.content,
              bold: true,
              alignment: 'left',
            },
          ],
        ],
      },
      margin: [0, 0, 0, 2],
    });
  }

  // Footer section
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const footerBody: any[] = [];
  if (commerce?.nombre) {
    footerBody.push([
      {
        text: commerce.nombre,
        fontSize: textSize.content,
        bold: true,
        alignment: 'center',
        margin: [0, 1, 0, 0],
      },
    ]);
  }

  footerBody.push([
    {
      text: 'Fecha y hora: ' + new Date(order.created_at).toLocaleString(),
      fontSize: textSize.content,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 0],
    },
  ]);

  contentSections.push({
    layout: 'noBorders',
    table: {
      widths: ['100%'],
      body: footerBody,
    },
    margin: [0, 0, 0, 0],
  });

  return contentSections;
};

export const generateKitchenPdf = async (
  commerce: Pick<Commerce, 'nombre'>,
  order: OrderType,
  products: ProductOrder[],
) => {
  const TEXT_MAP = {
    small: { title: 7, subTitle: 6, content: 4 },
    medium: { title: 9, subTitle: 8, content: 6 },
    large: { title: 11, subTitle: 10, content: 8 },
    extraLarge: { title: 13, subTitle: 12, content: 10 },
  } as const;
  const textSize = TEXT_MAP[(localStorage.getItem('printTextSize') as keyof typeof TEXT_MAP) || 'medium'];

  const content = createKitchenTicketContent(commerce, order, products, textSize);

  const docDefinition: TDocumentDefinitions = {
    pageSize: {
      width: (125 / 58) * parseInt(localStorage.getItem('printPageWidth') || '58'),
      height: 'auto',
    },
    pageMargins: [3, 3, 3, 3],
    defaultStyle: {
      lineHeight: 0.9,
    },
    content,
  };

  return pdfMake.createPdf(docDefinition);
};

// Generate a double PDF with both normal and kitchen tickets
export const generateDoublePdf = async (
  commerce: Pick<Commerce, 'nombre' | 'direccion' | 'address'>,
  order: OrderType,
  products: ProductOrder[],
  referencesLabel: string,
) => {
  const printDecoration = localStorage.getItem('printDecoration') !== 'false';
  const TEXT_MAP = {
    small: { title: 7, subTitle: 6, content: 4 },
    medium: { title: 9, subTitle: 8, content: 6 },
    large: { title: 11, subTitle: 10, content: 8 },
    extraLarge: { title: 13, subTitle: 12, content: 10 },
  } as const;
  const textSize = TEXT_MAP[(localStorage.getItem('printTextSize') as keyof typeof TEXT_MAP) || 'medium'];

  // Get content for both tickets
  const normalContent = createNormalTicketContent(
    commerce,
    order,
    products,
    referencesLabel,
    textSize,
    printDecoration,
  );
  const kitchenContent = createKitchenTicketContent(commerce, order, products, textSize);

  const docDefinition: TDocumentDefinitions = {
    pageSize: {
      width: (125 / 58) * parseInt(localStorage.getItem('printPageWidth') || '58'),
      height: 'auto',
    },
    pageMargins: [3, 3, 3, 3],
    defaultStyle: {
      lineHeight: 0.9,
    },
    content: [
      ...normalContent,
      { text: '', pageBreak: 'after' }, // Page break between tickets
      ...kitchenContent,
    ],
  };

  return pdfMake.createPdf(docDefinition);
};

// ==========================================
// ESC/POS Payload Builders (Plick Print Server)
// ==========================================

/**
 * Construye el payload ESC/POS del ticket usando el formato EXACTO de printUtils.ts
 * @param {OrderDetail} orderDetail - Detalle de la orden
 * @param {Partial<Commerce>} [commerce] - Información del comercio
 * @returns {string} String con comandos ESC/POS
 */
export const buildTicketPayload = (orderDetail: OrderDetail, commerce?: Partial<Commerce>): string => {
  const lines: string[] = [];
  const order = orderDetail.pedido;
  const productos = orderDetail.productos;

  // ========== SECCIÓN 1: CLIENT INFO (Centrado) ==========
  lines.push('\x1B\x61\x01'); // Centrar
  lines.push('Ticket pedido\n');
  lines.push(`No. ${order.folio}\n`);

  if (order.nombre) {
    lines.push('\nCliente:\n');
    lines.push(`${order.nombre}\n`);
  }

  if (order.numero_celular) {
    lines.push(`Celular: ${order.numero_celular}\n`);
  }

  if (order.numero_mesa) {
    lines.push(`Mesa: ${order.numero_mesa}\n`);
  }

  lines.push('\nDetalle del pedido:\n');

  // ========== SECCIÓN 2: PRODUCTS TABLE (Izquierda) ==========
  lines.push('\x1B\x61\x00'); // Alinear izquierda

  // Headers
  lines.push('Artículo                Total\n');
  lines.push('--------------------------------\n');

  productos.forEach((prd) => {
    // Línea principal del producto
    const prodLine = `${prd.cantidad} x ${prd.nombre} - ${prd.presentacion}`;
    const precio = `$${prd.subtotal}`;

    // Simular tabla: producto (ajustado) + precio (derecha)
    // Nota: 24 caracteres para nombre es una aproximación para 80mm/48chars o similar
    const paddedLine = prodLine.padEnd(24, ' ');
    lines.push(`${paddedLine}${precio}\n`);

    // Instrucciones
    if (prd.instrucciones) {
      lines.push(`Instrucciones: ${prd.instrucciones}\n`);
    }

    // Extras
    prd.extras.forEach((extra) => {
      const extraLine = `  ${extra.cantidad} x ${extra.nombre}`;
      const extraPrecio = `$${Number(extra.precio).toFixed(2)}`;
      const extraPadded = extraLine.padEnd(24, ' ');
      lines.push(`${extraPadded}${extraPrecio}\n`);
    });
  });

  // ========== SECCIÓN 3: TOTALS ==========
  lines.push('--------------------------------\n');
  // Usamos el 'subtotal' directo del objeto order
  lines.push(`Subtotal                $${Number(order.subtotal).toFixed(2)}\n`);

  if (order.coupon_amount) {
    lines.push(`Descuento             - $${Number(order.coupon_amount).toFixed(2)}\n`);
  }

  if (order.propina) {
    lines.push(`Propina                 $${Number(order.propina).toFixed(2)}\n`);
  }

  if (order.metodo_entrega === 'delivery' && order.costo_envio) {
    lines.push(`Costo de envío          $${Number(order.costo_envio).toFixed(2)}\n`);
  }

  lines.push(`Total a pagar           $${Number(order.total).toFixed(2)}\n`);

  // ========== SECCIÓN 4: PAYMENT INFO ==========
  if (order.comentario) {
    lines.push(`\nIndicaciones: ${order.comentario}\n`);
  }

  // Método de entrega
  const deliveryLabel =
    order.metodo_entrega === 'delivery'
      ? 'Domicilio'
      : order.metodo_entrega === 'inRestaurant'
        ? 'En restaurante'
        : 'Recoger';
  lines.push(`Metodo de entrega: ${deliveryLabel}\n`);

  // Método de pago
  let paymentLabel: string = order.metodo_pago;
  if (order.metodo_pago === 'toConfirm') {
    paymentLabel = order.metodo_entrega === 'inRestaurant' ? 'Por confirmar' : 'Pago en línea';
  } else if (order.metodo_pago === 'efectivo') {
    paymentLabel = 'Efectivo';
  } else if (order.metodo_pago === 'tarjeta') {
    paymentLabel = 'Tarjeta';
  }
  lines.push(`Tipo de pago: ${paymentLabel}\n`);

  // Si es efectivo, mostrar con cuánto paga y cambio
  if (order.metodo_pago === 'efectivo' && order.cliente_paga_con) {
    lines.push(`Cliente paga con: $${Number(order.cliente_paga_con).toFixed(2)}\n`);
    const cambio = Number(order.cliente_paga_con) - Number(order.total);
    if (cambio > 0) {
      lines.push(`Cambio: $${cambio.toFixed(2)}\n`);
    }
  }

  // ========== SECCIÓN 5: DELIVERY ADDRESS ==========
  if (order.metodo_entrega === 'delivery') {
    lines.push('\nDomicilio de entrega:\n');

    if (order.colonia) {
      lines.push(`Zona o Colonia: ${order.colonia}\n`);
    }
    if (order.calle) {
      lines.push(`Calle o Mza: ${order.calle}\n`);
    }
    if (order.numero_exterior) {
      lines.push(`Numero Exterior: ${order.numero_exterior}\n`);
    }
    if (order.numero_interior) {
      lines.push(`Numero interior: ${order.numero_interior}\n`);
    }
    if (order.direccion_completa) {
      lines.push(`${order.direccion_completa}\n`);
    }
    if (order.referencias) {
      lines.push(`Referencias: ${order.referencias}\n`);
    }
  }

  // ========== SECCIÓN 6: FOOTER (Centrado) ==========

  lines.push('\n');
  lines.push('\x1B\x61\x01'); // Centrar

  if (commerce?.nombre) {
    lines.push(`${commerce.nombre}\n`);
  }

  if (commerce?.address?.fullAddress || commerce?.direccion) {
    lines.push(`Con domicilio en ${commerce?.address?.fullAddress || commerce?.direccion}\n`);
  }

  lines.push(`Fecha y hora: ${new Date(order.created_at).toLocaleString('es-MX')}\n`);
  lines.push(
    '\nEste formato no es un comprobante de pago, ni contrato de venta, es para uso exclusivo del vendedor. Los precios que en el consignan son válidos hasta la fecha indicada.\n',
  );

  // Comando de corte
  lines.push('\n\n');
  lines.push('\x1D\x56\x00'); // Corte de papel

  return lines.join('');
};

/**
 * Construye el payload ESC/POS simplificado para COCINA
 * Replica EXÁCTAMENTE la estructura de createKitchenTicketContent de printUtils.ts
 * @param {OrderDetail} orderDetail - Detalle de la orden
 * @param {Partial<Commerce>} [commerce] - Información del comercio
 * @returns {string} String con comandos ESC/POS
 */
export const buildKitchenTicketPayload = (orderDetail: OrderDetail, commerce?: Partial<Commerce>): string => {
  const lines: string[] = [];
  const order = orderDetail.pedido;
  const productos = orderDetail.productos;

  // ========== SECCIÓN 1: ENCABEZADO (Centrado) ==========
  lines.push('\x1B\x61\x01'); // Centrar
  lines.push('\x1D\x21\x11'); // Doble altura y ancho
  lines.push('Ticket de Cocina\n');
  lines.push(`No. ${order.folio}\n`);
  lines.push('\x1D\x21\x00'); // Reset tamaño

  // Método de entrega (negrita, tamaño normal)
  const deliveryLabel =
    order.metodo_entrega === 'delivery'
      ? 'Domicilio'
      : order.metodo_entrega === 'inRestaurant'
        ? 'En restaurante'
        : 'Recoger';

  lines.push('\x1B\x45\x01'); // Negrita ON
  lines.push(`Metodo: ${deliveryLabel}\n`);
  lines.push('\x1B\x45\x00'); // Negrita OFF

  if (order.numero_mesa) {
    lines.push('\x1B\x45\x01'); // Negrita ON
    lines.push(`Mesa: ${order.numero_mesa}\n`);
    lines.push('\x1B\x45\x00'); // Negrita OFF
  }

  lines.push('\n'); // Margen similar a printUtils

  // ========== SECCIÓN 2: PRODUCTOS (Izquierda) ==========
  lines.push('\x1B\x61\x00'); // Alinear izquierda
  lines.push('\x1B\x45\x01'); // Negrita ON
  lines.push('Productos\n');
  lines.push('\x1B\x45\x00'); // Negrita OFF

  productos.forEach((prd) => {
    // Línea principal del producto (Negrita según printUtils)
    lines.push('\x1B\x45\x01'); // Negrita ON
    lines.push(`${prd.cantidad} x ${prd.nombre} - ${prd.presentacion}\n`);
    lines.push('\x1B\x45\x00'); // Negrita OFF

    // Instrucciones (con sangría y negrita)
    if (prd.instrucciones) {
      lines.push(`  Instrucciones: ${prd.instrucciones}\n`);
    }

    // Extras (con sangría)
    prd.extras.forEach((extra) => {
      lines.push(`  + ${extra.cantidad} x ${extra.nombre}\n`);
    });

    lines.push('\n'); // Espacio entre productos
  });

  // Indicaciones generales (si existen)
  if (order.comentario) {
    lines.push('\n');
    lines.push(`Indicaciones: ${order.comentario}\n`);
  }

  // ========== SECCIÓN 3: PIE (Centrado) ==========
  lines.push('\n');
  lines.push('\x1B\x61\x01'); // Centrar

  if (commerce?.nombre) {
    lines.push(`${commerce.nombre}\n`);
  }

  lines.push(`Fecha y hora: ${new Date(order.created_at).toLocaleString('es-MX')}\n`);

  // Comando de corte
  lines.push('\n\n');
  lines.push('\x1D\x56\x00'); // Corte de papel

  return lines.join('');
};
